import { Component } from "@kluntje/core";
import { find } from "@kluntje/js-utils/lib/dom-helpers";
import { scrollLock } from "Helper/domHelper";

/**
 * Custom Element handles flight search duties
 */
class FRAFlightsSearch extends Component {
  constructor() {
    super({
      ui: {
        form: ".fra-m-flights-search__form :-one",
        input: ".fra-m-flights-search-input :-one",
        typeToggle: "input[name=flighttype]",
        submitBtn: ".fra-m-flights-search__button",
        dateInput: "fra-e-datetime-picker :-one",
        timeInput: "fra-e-time-picker :-one",
        searchInput: ".fra-m-flights-search-input .fra-m-search__input :-one",
      },

      events: [
        {
          event: "fra-set-search-to-suggestion",
          target: "input",
          handler: "handleSuggestionSelection",
        },
        {
          event: "fra-show-more-results",
          target: "input",
          handler: "handleSubmit",
        },
        {
          event: "FRA_TAB_CHANGE",
          target: "window",
          handler: "handleTypeToggle",
        },
        {
          event: "change",
          target: "typeToggle",
          handler: "handleTypeToggle",
        },
        {
          event: "fra-e-datetime-picker:change",
          target: "dateInput",
          handler: "handleDateTimeInput",
        },
        {
          event: "fra-e-time-picker:change",
          target: "timeInput",
          handler: "handleTimeInput",
        },
        {
          event: "keyup",
          target: "searchInput",
          handler: "handleSearchInput",
        },
        {
          event: "submit",
          target: "form",
          handler: "handleSubmit",
        },
        {
          event: "click",
          target: "submitBtn",
          handler: "handleSubmit",
        },
      ],
      initialStates: {
        q: "",
        type: "",
        key: "",
        time: "",
        flighttype: "",
      },
      reactions: {
        q: ["handleStateChange"],
        time: ["handleStateChange"],
        flighttype: ["handleStateChange"],
      },
    });
  }

  static readonly DEFAULT_INITIAL_FLIGHTTYPE = "departures";

  /**
   * @returns {string} search term
   */
  get searchterm(): string {
    return this.state.q;
  }

  set searchterm(term: string) {
    this.ui.searchInput.value = term;
    this.setState({ q: term }, { silent: true });
  }

  /**
   * @returns {string} type of kewword search
   */
  get type(): string {
    return this.state.type;
  }

  set type(val: string) {
    let inputNameType = find(this.ui.form, "input[name='type']");
    if (!inputNameType) {
      inputNameType = document.createElement("input");
      inputNameType.setAttribute("type", "hidden");
      inputNameType.setAttribute("name", "type");
      this.ui.form.appendChild(inputNameType);
    }
    inputNameType.setAttribute("value", val);
    this.setState({ type: val }, { silent: true });
  }

  /**
   * Returns keyword
   * @returns {string} keyword
   */
  get key(): string {
    return this.state.key;
  }

  set key(val: string) {
    let inputNameKey = find(this.ui.form, "input[name='key']");
    if (!inputNameKey) {
      inputNameKey = document.createElement("input");
      inputNameKey.setAttribute("type", "hidden");
      inputNameKey.setAttribute("name", "key");
      this.ui.form.appendChild(inputNameKey);
    }
    inputNameKey.setAttribute("value", val);
    this.setState({ key: val }, { silent: true });
  }

  /**
   * Returns time formated as ISO
   * @returns {string} - ISO Date string as yyyy-mm-ddThh:mm:ss.sssZ
   */
  get time(): string {
    // stored value:   2019-11-22T14:00:00.000Z
    return this.state.time;
  }

  set time(val: string) {
    this.ui.dateInput.date = val;
    this.ui.timeInput.value = val;
    this.setState({ time: val }, { silent: true });
  }

  /**
   * @returns {string} - ISO Date string as yyyy-mm-ddThh:mm:ss.sssZ
   */
  get datepickerInput(): string {
    return this.ui.dateInput.date;
  }

  get timepickerInput(): string {
    return this.ui?.timeInput?.value || "00:00";
  }

  /**
   * Combine the date and time into a single string
   * @returns {string} - ISO Date string as yyyy-mm-ddThh:mm:ss.sssZ
   */
  get dateTime() {
    const dateTimeString = `${this.datepickerInput}T${this.timepickerInput}:00`;
    return new Date(dateTimeString).toISOString();
  }

  private isValidType(flighttype: string) {
    return flighttype === "departures" || flighttype === "arrivals";
  }

  /**
   * Returns type of flights, either "departures" or "arrivals"
   * @returns {string}
   */
  get flighttype(): string {
    return this.state.flighttype;
  }

  /**
   * Set type of flights, either "departures" or "arrivals"
   * @param {string} val "departures" or "arrivals"
   */
  set flighttype(val: string) {
    this.setState({ flighttype: val }, { silent: true });
  }

  /**
   * Return object with collected and manipulated FormData
   */
  get formData() {
    const result = {
      flighttype: this.flighttype,
      q: this.searchterm,
      time: this.dateTime,
      key: this.key,
      type: this.type,
    };

    // remove items which don't have any value
    Object.entries(result).forEach(([key, value]) => {
      if (value === "") delete result[key as keyof typeof result];
    });

    return result;
  }

  /**
   * Lifecycle-Hook
   */
  afterComponentRender() {
    setTimeout(() => this.prepareForm(), 500);
    if (window.location.pathname.includes("%2B")) this.handleSearchPreFill("%2B");
    else if (window.location.pathname.includes("+")) this.handleSearchPreFill("+");
    else if (sessionStorage.getItem("restoreFlightOverview")) {
      this.handleSearchRestoreFlightOverview(JSON.parse(sessionStorage.getItem("restoreFlightOverview")!));
    }
  }

  /*
   * Used for canonical tags to filter flights directly with url parameter
   */
  handleSearchPreFill(splitSign: string): void {
    const path = window.location.pathname.split("/").at(-1);
    const flightName = path ? path.split(splitSign).join(" ") : undefined;
    if (flightName) {
      this.searchterm = flightName;
      this.handleSubmit();
    }
  }

  handleSearchRestoreFlightOverview(restoreObject: any): void {
    this.ui.searchInput.value = restoreObject.formData.q ? restoreObject.formData.q : "";
  }

  /**
   * Publish update to form
   */
  handleStateChange(): void {
    this.dispatchEvent(new CustomEvent("fra-formdata-changed", { detail: { target: this } }));
  }

  /**
   * fill in input values / state, from the query params of the current page or the loaded html
   */
  prepareForm(): void {
    // set default value for flight type
    this.flighttype = (this.getAttribute("data-flighttype") as string) || FRAFlightsSearch.DEFAULT_INITIAL_FLIGHTTYPE;
    // check whether data has been submitted from widget
    if (document.location.search) {
      const inputQuery = new URL(document.location.href).searchParams;
      [...inputQuery].map(([qkey, qval]) => {
        switch (qkey) {
          case "flighttype":
            this.flighttype = qval;
            break;
          case "q":
            this.searchterm = qval;
            break;
          case "time":
            this.time = decodeURIComponent(qval);
            break;
          case "key":
            this.key = qval;
            break;
          case "type":
            this.type = qval;
            break;
          default:
            break;
        }
        return null;
      });
    } else {
      this.setState(
        {
          time: this.dateTime,
        },
        { silent: true },
      );
    }

    // get content
    this.dispatchEvent(new CustomEvent("fra-formdata-changed", { detail: { target: this } }));
  }

  /**
   * Handle form submit
   * - if from is standalone the form will submit to and load flight overview as defined in action
   * - if form is part of flight overview it will store all formdata in state and dispatch an event
   * @param {Event} e
   */
  handleSubmit(e?: Event): void {
    if (e) e.preventDefault();
    if (this.ui.form.getAttribute("data-action-arrivals") && this.ui.form.getAttribute("data-action-departures")) {
      const queryString = new URLSearchParams(this.formData).toString();
      const target = this.ui.form.getAttribute(`data-action-${this.flighttype}`);
      document.location.href = `${target}?${queryString}`;
    } else {
      this.dispatchEvent(new CustomEvent("fra-formdata-changed", { detail: { target: this } }));
    }
  }

  /**
   * Handle change of search term input, triggers fetch on overview
   * @param {KeyboardEvent} _e
   */
  handleSearchInput(_e?: KeyboardEvent): void {
    if (this.ui.searchInput.value !== this.searchterm) {
      this.type = "";
      this.key = "";
    }
    // set search term on input
    this.searchterm = this.ui.searchInput.value;

    if (_e && _e.key === "Enter") {
      this.handleSubmit();
    }
  }

  /*
   * Handle change of flight type tab, triggers fetch on overview
   */
  handleTypeToggle(e: CustomEvent): void {
    if (!this.isValidType(e?.detail)) return;
    this.flighttype = e.detail;
    const overviewUrl = this.flightsOverviewUrl(this.flighttype);
    if (overviewUrl !== "") {
      window.location.href = overviewUrl;
    } else {
      this.ui.input.setAttribute("data-flighttype", this.flighttype);
      this.setState({ flighttype: this.flighttype });
    }
  }

  /*
   * Returns provided api-url from custom element
   * @returns {string}
   */
  flightsOverviewUrl(flighttype: string): string {
    const flightOverviewUrl = `data-overview-${flighttype}`;
    return this.ui.form.getAttribute(flightOverviewUrl) || "";
  }

  /**
   * Handle change of time input
   * triggers fetch with/without time change
   */
  handleTimeInput(): void {
    // the AJAX service needs the date iso formatted (with the timezone)
    this.setState({ time: this.dateTime }, { silent: true });
    this.handleStateChange();
  }

  /**
   * Handle change of date input, triggers fetch on overview
   */
  handleDateTimeInput(): void {
    // the AJAX service needs the date iso formatted (with the timezone)
    this.setState({ time: this.dateTime });
  }
  /**
   * Handle update on selection of suggestion
   * - will also save values from suggesstion as hidden inputs
   *
   * @param {CustomEvent} e - custom event
   */
  handleSuggestionSelection(e: CustomEvent): void {
    scrollLock.pageBodyUnlock();
    this.key = e.detail.key;
    this.type = e.detail.type;
    this.searchterm = e.detail.q;
    this.dispatchEvent(new CustomEvent("fra-formdata-changed", { detail: { target: this } }));
  }
}

customElements.define("fra-m-flights-search", FRAFlightsSearch);

export default FRAFlightsSearch;
